import { fontFamilies, lightThemeColors } from "@magicbi/ui-library";
import { rawHex } from "../design-system/tokens/colors";
import { toCuratedFont } from "./curatedFonts";

/**
 * The colours and font a drawn visual (a Vega chart, a sandboxed custom visual) reads so it
 * matches the page's light or dark theme. Built from the active styled-components theme's tokens.
 */
export interface ChartTheme {
  mode: "light" | "dark";
  /** Primary text: labels and values. */
  text: string;
  /** Secondary text: value labels on marks, legend entries, captions. */
  muted: string;
  /** Tertiary text: axis tick labels (the design's text.low). */
  faint: string;
  /** Behind the plot (the card's surface). */
  background: string;
  /** A raised surface inside the plot (tooltips, cells). */
  surface: string;
  /** Hairlines: grid, axis domain, ticks. */
  border: string;
  /** The emphasis colour (a highlighted member, a single-series mark). */
  accent: string;
  /** Categorical series colours, in order. */
  palette: string[];
  font: string;
}

type ThemeColors = typeof lightThemeColors;

/** The part of a styled-components theme read here; anything missing falls back to the light tokens. */
export interface ChartThemeSource {
  mode?: string;
  colors?: Partial<ThemeColors>;
}

/** The active theme's tokens as a chart theme; no theme (e.g. outside a ThemeProvider) reads as light. */
export const toChartTheme = (source: ChartThemeSource | null | undefined): ChartTheme => {
  const colors = { ...lightThemeColors, ...(source?.colors ?? {}) } as ThemeColors;
  return {
    mode: source?.mode === "dark" ? "dark" : "light",
    text: colors.text.primary,
    muted: colors.text.secondary,
    faint: colors.text.tertiary,
    background: colors.background.primary,
    surface: colors.background.secondary,
    border: colors.border.stroke,
    accent: colors.primary[500],
    palette: [
      colors.primary[500],
      colors.primary[300],
      colors.accent.turquoise.main,
      colors.accent.peach.main,
      colors.accent.pink.main,
      colors.accent.green.main,
      colors.accent.purple.main,
    ],
    font: fontFamilies.sans,
  };
};

/**
 * Opt-in: the data app's default series colours (primary 500, warning, info), from the theme's tokens. Other charts keep `toChartTheme`'s palette.
 * A look's own palette still wins when applied after this (`withChartLook` / `withDrawingLook`).
 */
export const withDataAppPalette = (theme: ChartTheme, source: ChartThemeSource | null | undefined): ChartTheme => {
  const colors = { ...lightThemeColors, ...(source?.colors ?? {}) } as ThemeColors;
  return {
    ...theme,
    accent: colors.primary[500],
    palette: [colors.primary[500], rawHex.accent.caution.solid, rawHex.accent.infoBlue.solid],
  };
};

/** A visual's own look over the page theme: its own series colours and web font. */
export interface ChartLook {
  /** 2–12 `#rrggbb` colours; anything else keeps the theme's palette. */
  palette?: readonly string[] | null;
  /** A curated web font family; anything else keeps the theme's font. */
  font?: string | null;
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const PALETTE_MIN = 2;
const PALETTE_MAX = 12;

const isPalette = (palette: readonly string[] | null | undefined): palette is readonly string[] =>
  Array.isArray(palette) &&
  palette.length >= PALETTE_MIN &&
  palette.length <= PALETTE_MAX &&
  palette.every((colour) => typeof colour === "string" && HEX_COLOR.test(colour));

/**
 * The theme with a look applied (in light and dark alike): a valid palette replaces the theme's,
 * a curated font goes first in the font stack with the theme's stack as its fallback.
 */
export const withChartLook = (theme: ChartTheme, look: ChartLook | null | undefined): ChartTheme => {
  const font = toCuratedFont(look?.font);
  const palette = look?.palette;
  return {
    ...theme,
    palette: isPalette(palette) ? [...palette] : theme.palette,
    font: font ? `"${font}", ${theme.font}` : theme.font,
  };
};

/* ---------------------------------------------------------------------------------------------
 * A drawing's own look: a deck theme's or a brand's (live previews of distributed copies)
 * ------------------------------------------------------------------------------------------- */

/**
 * A drawing's own look over the page theme, e.g. a deck theme's or a brand's: its series colours,
 * any loaded font family (a theme's own font files, not only the curated list) and its text
 * colours. Structurally the chart half of the TilePreview molecule's look.
 *
 * Colours follow action-engine's `branded_vega`, so a preview matches the file: the palette is the
 * spec's default category range and its first colour the default mark colour; anything the spec
 * sets itself (its own `config.range` / `config.mark` colours, a scale's `range` or `scheme`)
 * wins. `withDrawingLook` + VegaChart's `withChartTheme` apply exactly that.
 */
export interface ChartDrawingLook {
  /** 2–12 `#rrggbb` colours; anything else keeps the theme's palette. */
  palette?: readonly string[] | null;
  /** A font family the page has loaded; a name with quotes or CSS punctuation is ignored. */
  fontFamily?: string | null;
  /** `#rrggbb` for titles and values. */
  textColor?: string | null;
  /** `#rrggbb` for axes, labels and captions. */
  mutedColor?: string | null;
}

/** Letters, digits, spaces, `_ . ' -`: a family name that can't break out of a quoted CSS value. */
const FONT_FAMILY = /^[\p{L}\p{N} _.'-]{1,80}$/u;

/** A font family that is safe to quote into a font stack, trimmed; null for anything else. */
export const toSafeFontFamily = (family: string | null | undefined): string | null => {
  const trimmed = typeof family === "string" ? family.trim() : "";
  return FONT_FAMILY.test(trimmed) ? trimmed : null;
};

/** A `#rrggbb` colour, or null for anything else. */
export const toHexColor = (colour: string | null | undefined): string | null =>
  typeof colour === "string" && HEX_COLOR.test(colour) ? colour : null;

/** A look's palette when it is a valid one (2–12 `#rrggbb`), as a copy; null otherwise. */
export const toLookPalette = (palette: readonly string[] | null | undefined): string[] | null =>
  isPalette(palette) ? [...palette] : null;

/**
 * The theme with a drawing's look applied: a valid palette replaces the theme's series colours and
 * its first colour becomes the single-series colour (a brand's accent comes first in its palette);
 * a safe font family goes first in the font stack; valid text colours replace the theme's.
 * Anything missing or invalid keeps the theme's own.
 */
export const withDrawingLook = (theme: ChartTheme, look: ChartDrawingLook | null | undefined): ChartTheme => {
  if (!look) return theme;
  const palette = toLookPalette(look.palette);
  const family = toSafeFontFamily(look.fontFamily);
  return {
    ...theme,
    text: toHexColor(look.textColor) ?? theme.text,
    muted: toHexColor(look.mutedColor) ?? theme.muted,
    faint: toHexColor(look.mutedColor) ?? theme.faint,
    accent: palette ? palette[0] : theme.accent,
    palette: palette ?? theme.palette,
    font: family ? `"${family}", ${theme.font}` : theme.font,
  };
};

/**
 * The colours a chart's series take in turn, as action-engine picks them for the file
 * (`distribution/mapping.chart_colours`): the palette, with the accent in its first place when
 * one is set. Empty with no palette. A look's palette is this list; its first colour is the
 * single-series colour.
 */
export const chartColours = (accent: string | null | undefined, palette: readonly string[]): string[] =>
  palette.length ? [accent || palette[0], ...palette.slice(1)] : [];
